import "./Account.scss";
import {Redirect, useLocation, useHistory} from "react-router-dom";
import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import PropTypes from "prop-types";
import {Modal} from "bootstrap";
import {DateTime} from "luxon";
import Helpers from "../../services/Helpers";
import ProfileForm from "../../views/Member/components/ProfileForm";
import PasswordForm from "../../views/Member/components/PasswordForm";
import Subscription from "./components/Subscription";
import Preferences from "../../views/Member/components/Preferences";
import PurchaseHistory from "../../views/Member/components/PurchaseHistory";
import Footer from "../../views/components/Footer";
import BillingInformationAccordion
  from "../../views/Member/components/BillingInformationAccordion";
import SubscriptionCancellationForm
  from "./modals/SubscriptionCancellationForm";
import NewSubscriptionForm from "./modals/NewSubscriptionForm";
import EditSubscriptionForm from "./modals/EditSubscriptionForm";
import StartTrialForm from "./modals/StartTrialForm";
import SubscriptionMessage from "./components/SubscriptionMessage";
import {PersonalizedStats} from "./components/PersonalizedStats";
import axios from "axios";
import {GetBackToAccount} from "./components/GetBack";

export default function Account(props) {
  const location = useLocation();
  const history = useHistory();
  const isInternalUpdate = useRef(false);
  const isHiding = useRef(false);
  const { formatDateInternational, isNotEmpty, pageTitle, profilePhoto } = Helpers();
  const [working, setWorking] = useState(false);
  const [accountConfigPage, setAccountConfigPage] = useState(() => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get("s") || "default";
  });

  const hideAccountConfigPage = () => {
    isHiding.current = true;
    setAccountConfigPage("default");
  }

  const handlePageChange = (newPage) => {
    isInternalUpdate.current = true;
    setAccountConfigPage(newPage);
  };

  const getBack = () => {
    return () => {
      isHiding.current = true;
      setAccountConfigPage("default");
      window.scrollTo({ top: window.scrollY - 500, behavior: "smooth" });
    }
  }

const [stats, setStats] = useState(null);

useEffect(() => {
  axios({
    method: "get",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${props.token}`,
    },
    url: `${process.env.REACT_APP_API}/personalized-content/stats`,
  })
    .then((res) => setStats(res.data))
    .catch((error) => {
      console.log("Error fetching personalized stats:", error);
      setStats(undefined);
    });
}, []);

  const newSubscriptionRef = useRef(null);

  useEffect(() => {
    if (accountConfigPage === "addSubscription" && newSubscriptionRef.current) {
      const elementPosition = newSubscriptionRef.current.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({ top: elementPosition - 55, behavior: "smooth" });
    }
  }, [accountConfigPage]);

  useEffect(() => {
    if (!isInternalUpdate.current && !isHiding.current) {
      const searchParams = new URLSearchParams(location.search);
      const urlPage = searchParams.get("s") || "default";

      if (urlPage !== accountConfigPage) {
        setAccountConfigPage(urlPage);
      }
    } else {
      isInternalUpdate.current = false;
      isHiding.current = false;
    }
  }, [location.search, accountConfigPage]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const currentUrlPage = searchParams.get("s");

    if (accountConfigPage !== currentUrlPage) {
      isInternalUpdate.current = true;
      if (accountConfigPage === "default") {
        history.replace("/account");
      } else {
        history.replace(`/account?s=${accountConfigPage}`);
      }
    }
  }, [accountConfigPage, history, location.search]);

  useEffect(() => {
    return () => {
      // cleanup modal stuff
      const backdrop = document.querySelector(".modal-backdrop");
      if (isNotEmpty(backdrop)) {
        backdrop.remove();
      }
      document.body.classList.remove("modal-open");
      document.body.removeAttribute("style");
    };
  }, []);

  useEffect(() => {
    document.title = pageTitle(location.pathname);
  }, [location]);

  const showPictureModal = useCallback(() => {
    const element = document.getElementById("picture-modal");
    const pictureModal = Modal.getOrCreateInstance(element);
    pictureModal.show();
  }, []);

  const hidePictureModal = useCallback(() => {
    const element = document.getElementById("picture-modal");
    const pictureModal = Modal.getOrCreateInstance(element);
    pictureModal.hide();
    const pictureInput = document.getElementById("picture-input");
    pictureInput.value = "";
  }, []);

  const subStore = props.subStore;
  const currentSub = subStore.subscription.length > 0 ? subStore.subscription[0] : null;
  const nextBillingDate = currentSub && currentSub.nextBillingDate ? DateTime.fromSeconds(currentSub.nextBillingDate) : null;
  const lastDay = nextBillingDate ? nextBillingDate.minus({ days: 1 }).toFormat("MMMM d, yyyy") : "";

  // scroll to top on subscription message
  const subscriptionMessageRef = useRef(null);

  useEffect(() => {
    if (subStore && subStore.subscriptionMessage && subscriptionMessageRef.current) {
      const elementPosition = subscriptionMessageRef.current.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({ top: elementPosition - 55, behavior: "smooth" });
    }
  }, [subStore.subscriptionMessage]);

  const closePictureModalButton = useMemo(
    () =>
      working ? (
        <button type="button" className="btn-close" disabled={true} />
      ) : (
        <button type="button" className="btn-close" onClick={hidePictureModal} />
      ),
    [working, hidePictureModal]
  );

  const pictureUploadButton = useMemo(
    () =>
      working ? (
        <button className="btn btn-primary" type="button" disabled={true}>
          Upload
        </button>
      ) : (
        <button
          className="btn btn-primary"
          type="button"
          onClick={() => {
            setWorking(true);
            const files = document.getElementById("picture-input").files;
            props.memberStore.updatePicture(files, () => {
              setWorking(false);
              hidePictureModal();
            });
          }}
        >
          Upload
        </button>
      ),
    [working, hidePictureModal, props.memberStore]
  );

  function memberSince() {
    let result = null;
    if (props.memberStore.member.hasMP && (!props.memberStore.member.hasTrial || props.memberStore.member.hadMP)) {
      if (isNotEmpty(props.memberStore.member.since)) {
        const since = formatDateInternational(props.memberStore.member.since);
        if (since < "2019-11-01") {
          result = (
            <div>  
            <img src={`${process.env.PUBLIC_URL}/images/Original-Dirty-Smear.png`} height="50" alt="OG Dirty Badge" />
              <p className="mt-1">
                Thank you for being an OG Dirty and eating more plants and celebrating human imperfection with us since {DateTime.fromSeconds(props.memberStore.member.since).toFormat("MMMM d, yyyy")}!
              </p>
            </div>
          );
        } else {
          result = (
            <div>
              <p>
               Thank you for eating more plants and celebrating human imperfection with us since {DateTime.fromSeconds(props.memberStore.member.since).toFormat("MMMM d, yyyy")}.
              </p>
            </div>
          );
        }
      }
    } else {
      result = (
          <div>
            <p>
              Thank you for eating more plants and celebrating human imperfection with us.
            </p>
          </div>
      );
    }
    return result;
  }

 const renderAccountConfigPage = useCallback(() => {
    switch (accountConfigPage) {
      case "cancel":
        return (
          <SubscriptionCancellationForm
            hasMP={props.memberStore.hasMP}
            lastDay={lastDay}
            cancel={props.subStore.cancelSubscription}
            hide={hideAccountConfigPage}
            shouldReset={false}
          />
        );
      case "addSubscription":
        return (
          <NewSubscriptionForm
            memberStore={props.memberStore}
            subStore={props.subStore}
            reactivating={false}
            hide={hideAccountConfigPage}
          />
        );
      case "reactivateSubscription":
        return (
          <NewSubscriptionForm
            memberStore={props.memberStore}
            subStore={props.subStore}
            reactivating={true}
            hide={hideAccountConfigPage}
          />
        );
      case "startTrial":
        return (
          <StartTrialForm
            memberStore={props.memberStore}
            subStore={props.subStore}
            hide={hideAccountConfigPage}
          />
        );
      case "changeSubscription":
        return (
          <EditSubscriptionForm
            member={props.memberStore.member}
            subStore={props.subStore}
            currentPlan={
              props.subStore.subscription.length > 0
                ? props.subStore.subscription[0]
                : null
            }
            hide={hideAccountConfigPage}
          />
        );
      default:
        return (
          <>
            {props.memberStore.member.hasMP || props.memberStore.member.hasRV ? (
              <Preferences cache={props.cache} memberStore={props.memberStore} />
            ) : null}
            <ProfileForm
              message={props.message}
              member={props.memberStore.member}
              resetMessage={props.resetMessage}
              update={props.memberStore.updateMember}
            />
            <PasswordForm
              member={props.memberStore.member}
              changePassword={props.memberStore.changePassword}
            />
            <BillingInformationAccordion
              memberStore={props.memberStore}
              subStore={props.subStore}
            />
            <PurchaseHistory memberStore={props.memberStore} />
          </>
        );
    }
  }, [accountConfigPage, props]);

  return props.authenticated ? (
    <>
      <main id="account" className="container-lg">
       

{/*/!* //if not current member *!/REMOVED AS A PART OF RELEASE 02_03_2026*/}
{/*        {props.memberStore.member.hasMP ? null : (*/}
{/*          <div className="text-center align-items-center d-sm-none d-md-block">*/}
{/*            <p className="text-muted px-4 px-md-2">We can't wait to help you get that plant glow!</p>*/}
{/*          </div>*/}
{/*        )}*/}

         {/* Profile */}

         <div className="container-fluid py-4">
  <div className="row g-4 justify-content-center align-items-start">

    {/* Profile */}
    <div className="col-12 col-md-4">
      <div className="card border-0 shadow-sm text-center p-4 h-100">
        <div className="position-relative mx-auto">
          <h5 className="fw-semibold mb-3 text-center">
          Hello, {props.memberStore.member.firstname}
        </h5>
          <img
            src={profilePhoto(props.memberStore.member)}
            className="img-fluid rounded-circle shadow-sm"
            alt={`${props.memberStore.member.firstname}'s profile`}
            style={{ width: '100px', height: '100px', objectFit: 'cover' }}
          />
          <button
            className="btn btn-primary btn-sm shadow position-absolute"
            style={{
              bottom: '6px',
              right: '19px',
              borderRadius: '50%',
              width: '30px',
              height: '30px',
            }}
            type="button"
            onClick={showPictureModal}
          >
            <span className="fas fa-pencil-alt" />
          </button>
        </div>

       <p className="mb-0 small mt-2">{memberSince()}</p>
       
      </div>
    </div>

   

    {/* Stats */}
    <div className="col-12 col-md-4">
      <div className="card border-0 shadow-sm p-4 h-100 text-center text-md-start">
        <h5 className="fw-semibold mb-3 text-center">
          Your stats
        </h5>
        <PersonalizedStats stats={stats} />
              </div>
    </div>


     {/* Subscription */}
    <div className="col-12 col-md-4">
      <div className="card border-0 shadow-sm p-4 h-100 text-center text-md-start">
        <h5 className="fw-semibold mb-3 text-center">
          Your membership
        </h5>
        <Subscription
          memberStore={props.memberStore}
          subStore={props.subStore}
          setAccountConfigPage={handlePageChange}
        />
         
      </div>
    </div>

  </div>
</div>



        <div className="row" ref={newSubscriptionRef}>
          <div className="accordion mb-3 card-body">
          {accountConfigPage !== 'default' && <GetBackToAccount getBack={getBack()} />}
            {!!subStore.subscriptionMessage && <div ref={subscriptionMessageRef}><SubscriptionMessage {...props} memberStore={props.memberStore} subStore={props.subStore} /></div>}
            {accountConfigPage !== "default" ? (
                <div className={`container-lg bg-white additional-padding`}>
                  {renderAccountConfigPage()}
                </div>
            ) : renderAccountConfigPage()}
          </div>
        </div>

        <div className="modal fade" id="picture-modal" tabIndex={-1} data-bs-backdrop="static">
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Upload a picture</h5>
                {closePictureModalButton}
              </div>
              <div className="modal-body">
                <div className="input-group mb-3">
                  <input type="file" className="form-control" id="picture-input" />
                  {pictureUploadButton}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  ) : (
    <Redirect to="/sign-in" />
  );
}

Account.propTypes = {
  authenticated: PropTypes.bool,
  message: PropTypes.string,
  cache: PropTypes.object,
  memberStore: PropTypes.object,
  subStore: PropTypes.object,
  resetMessage: PropTypes.func,
};
